import React, { useState, useContext } from 'react';
import { useParams } from 'react-router';
import { useTrackableItemQuery } from '../../graphql/queries/trackableItem.generated';
import { useUpdateTrackableItemMutation } from '../../graphql/mutations/updateTrackableItem.generated';
import ClassicLoadingSpinner from '../../components/LoadingSpinner/ClassicLoadingSpinner';
import i18n from '@/utils/i18n';
import { ContentSection } from '../../components/Layout/ContentSection';
import { MainHeader } from '../../components/Layout/MainHeader';
import { Button } from 'reactstrap';
import ItemTypeSelector from '../../components/TrackableItems/ItemTypeSelector';
import { FaCopy, FaSave } from 'react-icons/fa';
import { LayoutStoreInstance } from '../../LayoutStore';
import QRCodeStyling from 'qr-code-styling';
import ItemLocationHistory from '../../components/ItemLocationHistory';
import TrackableItemStatusBadge from '../../components/TrackableItem/TrackableItemStatusBadge';
import { ViewerContext } from '../../providers/ViewerContextProvider';
import TrackableItemDisplay from '../../components/TrackableItem/TrackableItemDisplay';
import { Role } from '../../contants';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export type TrackableItemWithQrCode = {
  id: string;
  name: string;
  item_type: {
    id: number;
    name: string;
    category: string;
    archived: boolean;
    image_url?: string;
    color_hex_code?: string;
  };
  qrCode: string;
};

export const TrackableItemDetailPage: React.FC = () => {
  const { itemId } = useParams<{ itemId: string }>();
  const context = useContext(ViewerContext);
  const readOnly = context.role === Role.eventCleaningEmployee;
  const [trackableItem, setTrackableItem] = useState<TrackableItemWithQrCode>();

  useDocumentTitle('PageTitles:asset', trackableItem?.name);

  const { loading } = useTrackableItemQuery({
    skip: itemId == null,
    variables: { id: itemId || '' },
    fetchPolicy: 'no-cache',
    onCompleted: async (d) => {
      if (d.trackableItem) {
        const withQrCode = await addTrackableItemQrCode(d.trackableItem);
        setTrackableItem(withQrCode);
      }
    },
    onError: (error: any) => {
      LayoutStoreInstance.setError(
        i18n.t('TrackableItems:errorLoading') + ': ' + error.message,
      );
    },
  });

  async function addTrackableItemQrCode(item: any) {
    const itemWithQrCode = {
      ...item,
      qrCode: '' as string,
    };
    if (!itemWithQrCode.qrCode) {
      itemWithQrCode.qrCode = await getTrackableItemQRCode(item.id);
    }
    return itemWithQrCode;
  }

  async function getTrackableItemQRCode(id: string) {
    const qrCode = getQrCodeStyling(id);
    const blob: any = await qrCode.getRawData('png');
    return window.URL.createObjectURL(blob);
  }

  function getQrCodeStyling(id: string) {
    return new QRCodeStyling({
      width: 100,
      data: `HTTP://VYT.TO/TI/${id}`,
      height: 100,
      dotsOptions: {
        color: '#000000',
        type: 'square',
      },
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: 10,
      },
      backgroundOptions: {
        color: '#ffffff',
      },
      qrOptions: {
        errorCorrectionLevel: 'Q',
      },
    });
  }

  const [updateTrackableItem, { loading: updating }] =
    useUpdateTrackableItemMutation({
      onCompleted: async (d) => {
        if (d.updateTrackableItem) {
          const withQrCode = await addTrackableItemQrCode(
            d.updateTrackableItem,
          );
          setTrackableItem(withQrCode);
          LayoutStoreInstance.setSuccess(
            i18n.t('TrackableItems:updateSuccess'),
          );
        }
      },
      onError: (error: any) => {
        LayoutStoreInstance.setError(
          i18n.t('TrackableItems:updateError') + ': ' + error.message,
        );
      },
    });

  async function save() {
    if (trackableItem != null) {
      await updateTrackableItem({
        variables: {
          id: trackableItem.id,
          update: {
            name: trackableItem.name,
            item_type_id: trackableItem.item_type.id,
          },
        },
      });
    }
  }

  if (loading || trackableItem?.id == null) {
    return <ClassicLoadingSpinner />;
  }

  return (
    <div>
      <MainHeader title={i18n.t('TrackableItems:trackableItem')} />
      <ContentSection title={i18n.t('Common:generalInfo')} collapsable={false}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ width: '66%' }}>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  {i18n.t('TrackableItems:name')}
                </span>
              </div>
              <input
                className="form-control"
                type="text"
                disabled={readOnly}
                onChange={(e) =>
                  setTrackableItem({
                    ...trackableItem,
                    name: e.target.value,
                  })
                }
                value={trackableItem.name}
              />
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(trackableItem.name);
                  LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                }}
              >
                <FaCopy />
              </Button>
            </div>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text">{i18n.t('Common:id')}</span>
              </div>
              <input
                className="form-control"
                type="text"
                disabled
                value={trackableItem.id}
              />
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(trackableItem.id);
                  LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                }}
              >
                <FaCopy />
              </Button>
            </div>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  {i18n.t('TrackableItems:itemType')}
                </span>
              </div>
              <ItemTypeSelector
                value={trackableItem.item_type.id.toString()}
                onChange={(value) => {
                  setTrackableItem({
                    ...trackableItem,
                    item_type: {
                      ...trackableItem.item_type,
                      id: parseInt(value, 10),
                    },
                  });
                }}
                disabled={readOnly}
              />
            </div>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  {i18n.t('TrackableItems:category')}
                </span>
              </div>
              <input
                className="form-control"
                type="text"
                disabled
                value={trackableItem.item_type.category}
              />
            </div>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  {i18n.t('TrackableItems:status')}
                </span>
              </div>
              <div className="form-control d-flex align-items-center">
                <TrackableItemStatusBadge
                  archived={trackableItem.item_type.archived}
                />
              </div>
            </div>
          </div>
          <div className="mb-2 me-2 mt-2">
            <div className="container-image-container-xl mb-3 bg-white p-1">
              <img
                src={trackableItem.qrCode}
                alt={`QR code for ${trackableItem.name}`}
              />
            </div>
            <TrackableItemDisplay
              trackableItem={trackableItem}
              showTrackableItemId={false}
              showLink={false}
              showName={false}
              size={'xl'}
            />
          </div>
        </div>
        <Button className="float-end mt-2" onClick={save} disabled={readOnly}>
          <FaSave className="me-1" />
          {i18n.t('Common:save')}
        </Button>
      </ContentSection>
      <ItemLocationHistory itemId={trackableItem.id} />
    </div>
  );
};
